import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { PaperMetadataProps } from './types';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold, interMedium, interRegular } from "../../lib/fonts";
import LaTeXText from '../LaTeXText';
import { formatPaperAuthors } from '../../lib/paperAuthors';
import { getPaperReferenceLabel } from '../../lib/sourceHelpers';
import { Trans } from '@lingui/react/macro';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';

/**
 * PaperMetadata displays the paper's title, authors, publication date, and venue
 * with refined editorial styling
 */
export default function PaperMetadata({ paper, accessStatus }: PaperMetadataProps) {
    const { fontsLoaded } = useFontLoading();
    const { formatLocale } = useAppLocale();

    // Format date as "Jan 15, 2025" (shorter format)
    const formatDate = (dateString: string | null) => {
        if (!dateString) return null;

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(formatLocale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formattedDate = formatDate(paper.published_date);
    const referenceLabel = paper.reference_label || getPaperReferenceLabel({
        sourceId: paper.source_id,
        sourceName: paper.source?.name,
        sourcePaperId: paper.source_paper_id,
        doi: paper.doi,
        url: paper.url,
    });

    return (
        <View style={styles.container}>
            {/* Paper title */}
            <LaTeXText
                text={paper.title}
                style={[
                    styles.title,
                    fontsLoaded && interSemiBold
                ]}
                fontsLoaded={fontsLoaded}
            />

            {/* Authors */}
            <Text style={[styles.authors, fontsLoaded && interRegular]}>
                {formatPaperAuthors(paper.authors)}
            </Text>

            {/* Metadata row: clean inline format */}
            <View style={styles.metadataRow}>
                {/* Primary paper reference */}
                {referenceLabel && (
                    <Text style={[styles.sourceId, fontsLoaded && interMedium]}>
                        {referenceLabel}
                    </Text>
                )}

                {/* Publication date */}
                {formattedDate && (
                    <>
                        {referenceLabel && <Text style={styles.separator}>·</Text>}
                        <Text style={[styles.metadataText, fontsLoaded && interRegular]}>
                            {formattedDate}
                        </Text>
                    </>
                )}

                {/* Access status badge */}
                {(accessStatus === 'checking' || accessStatus === 'closed' || accessStatus === 'unavailable') && (
                    <>
                        {(referenceLabel || formattedDate) && (
                            <Text style={styles.separator}>·</Text>
                        )}
                        {accessStatus === 'checking' ? (
                            <ActivityIndicator size="small" color={colors.text500} style={styles.accessSpinner} />
                        ) : (
                            <View style={styles.closedAccessBadge}>
                                <MaterialIcons
                                    name={accessStatus === 'unavailable' ? 'block' : 'lock'}
                                    size={12}
                                    color="#92400e"
                                />
                                <Text style={[styles.closedAccessText, fontsLoaded && interMedium]}>
                                    {accessStatus === 'unavailable' ? <Trans id="papers.papermetadata.fullTextUnavailable" comment="Conditional UI copy in components/papers/PaperMetadata.tsx">Full text unavailable</Trans> : <Trans id="papers.papermetadata.closedAccess" comment="Conditional UI copy in components/papers/PaperMetadata.tsx">Closed Access</Trans>}
                                </Text>
                            </View>
                        )}
                    </>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 28,
    },
    title: {
        fontSize: 26,
        fontWeight: '700',
        color: colors.text900,
        lineHeight: 34,
        marginBottom: 12,
        letterSpacing: -0.3,
    },
    authors: {
        fontSize: 15,
        color: colors.text600,
        lineHeight: 22,
        marginBottom: 10,
    },
    metadataRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    sourceId: {
        fontSize: 13,
        color: colors.text500,
        fontWeight: '500',
    },
    separator: {
        fontSize: 13,
        color: colors.mediumGray,
        marginHorizontal: 8,
    },
    metadataText: {
        fontSize: 13,
        color: colors.text500,
    },
    closedAccessBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#fef3c7',
        borderRadius: 6,
        paddingHorizontal: 7,
        paddingVertical: 2,
        gap: 4,
    },
    closedAccessText: {
        fontSize: 12,
        color: '#92400e',
        fontWeight: '500',
    },
    accessSpinner: {
        transform: [{ scale: 0.7 }],
    },
});
